<template>
	<header class="header">
		<div class="logo-container">
			<img :src="require('@/assets/buzzztv_logo.png')" alt="BuzzzTV" />
		</div>
		<div class="filter-container header-filter-toolbar">
			<CompanySiteFilter v-if="$store.getters.filterOptions && !isAdminRoute" @input="handleInput" />
			<transition name="fade">
				<TimeFilter v-if="isFilterEnabled" class="filter-time-grow" @input="handleInput" />
			</transition>
			<div
				class="filter-button-wrapper"
				:class="{ 'top-right': isAdminRoute }"
				:style="{ 'margin-inline-end': isFilterEnabled ? '' : '20px' }"
			>
				<transition name="fade">
					<button
						type="button"
						class="filter-button"
						:class="{ 'active-filter': isActiveFilter }"
						v-if="isFilterEnabled"
						@click="toggleAdditionalFilter"
						:title="$t('filters') || 'Show filters'"
						:aria-label="$t('filters') || 'Show filters'"
					>
						<img :src="require('@/assets/filter.svg')" alt="" aria-hidden="true" />
						<span v-if="isActiveFilter" class="filter-badge" aria-hidden="true"></span>
					</button>
				</transition>
			</div>
		</div>
	</header>
</template>

<script>
import TimeFilter from './TimeFilter'
import CompanySiteFilter from './CompanySiteFilter'
import { mapActions, mapMutations, mapGetters } from 'vuex'

export default {
	data: () => ({
		logoUrl: '',
		shouldUpdate: true
	}),
	methods: {
		...mapActions(['getOverviewData', 'getAnalyticsData', 'loadUpdatedOptions']),
		...mapMutations(['updateFilter']),
		async handleInput(field, value) {
			const { roomId } = this.$route.params
			if (!this.shouldUpdate) return
		this.shouldUpdate = false
		this.updateFilter({ field, value })
		setTimeout(() => (this.shouldUpdate = true), 0)
		
		// For company and site changes, let Dashboard watchers handle the API calls
		// to avoid duplicate calls and ensure proper data loading sequence
		if (field === 'company' || field === 'site') {
			// Only load updated options, don't fetch data
			await this.loadUpdatedOptions();
			return;
		}

		// Date changes are loaded by Dashboard. Fetching here as well races that
		// load: applying custom dates used to emit timeType while the store still
		// held the previous preset, and the slower response replaced the new range.
		if (field === 'time' || field === 'timeType') {
			if (field === 'time') {
				this.$store.commit({ type: 'setFilterJustApplied', value: true });
				this.$nextTick(() => {
					this.$nextTick(() => {
						this.$store.commit({ type: 'setFilterJustApplied', value: false });
					});
				});
			}
			return;
		}
		
		// Capture filter state after mutation
		const filterAfterUpdate = { ...this.filterSelected, roomId: roomId ? roomId : '' };
			
			this.$store.commit({ type: 'toggleLoading', value: true })
			await Promise.all([
				this.getOverviewData({ filter: filterAfterUpdate }),
				this.getAnalyticsData({ filter: filterAfterUpdate }),
				this.loadUpdatedOptions()
			])
			this.$store.commit({ type: 'toggleLoading', value: false })
		},
		toggleAdditionalFilter() {
			if (!this.isFilterDisabled) return
			this.$store.commit('toggleAdditionalFilter', {})
		}
	},
	computed: {
		...mapGetters(['filterSelected']),
		isFilterAvailable() {
			return this.$route.path.includes('dashboard')
		},
		isFilterDisabled() {
			return true
		},
		isFilterEnabled() {
			return [
				'performance',
				'analytics',
				'cleaningWorkPlan',
				'roomAnalyticsLog',
				'cleanLog',
				'maintenanceLog',
				'roomAnalytics',
				'cleanerArrival',
				'roomExposures',
				'connectivity',
				'power'
			].includes(this.$route.name)
		},
		isAdminRoute() {
			return (
				this.$route.path.includes('admin') &&
				!this.$route.path.includes('devices') &&
				!this.$route.path.includes('room')
			)
		},
		isActiveFilter() {
			return this.$store.getters.isActiveFilter
		}
	},
	components: { TimeFilter, CompanySiteFilter }
}
</script>

<style lang="scss" scoped>
@use '@/styles/mixins' as *;

.header {
	height: 80px;
	backdrop-filter: blur(20px);
	-webkit-backdrop-filter: blur(20px);
	border-bottom: 1px solid rgba(241, 245, 249, 0.8);
	display: grid;
	grid-template-columns: auto minmax(0, 1fr);
	align-items: center;
	padding: 0 40px;
	gap: 40px;
	top: 0;
	z-index: 100;
	overflow: visible;
	transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

	@media (max-width: 1024px) {
		gap: 32px;
		padding: 0 32px;
	}

	@media (max-width: 768px) {
		height: 72px;
		padding: 0 24px;
		gap: 24px;
	}

	@media (max-width: 640px) {
		height: 72px;
		padding: 0 16px;
		gap: 16px;
	}

	@media (max-width: 480px) {
		padding: 0 12px;
		gap: 12px;
		height: 68px;
	}

	&::before {
		content: '';
		position: absolute;
		inset: 0;
		background: $white;
		pointer-events: none;
	}

	&:hover {
		box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
		transform: translateY(-1px);
	}

	&:active {
		transform: translateY(0);
	}
}

.logo-container {
	@include align-center;
	position: relative;
	flex-shrink: 0;
	z-index: 1;

	&::after {
		content: '';
		position: absolute;
		right: -20px;
		top: 50%;
		transform: translateY(-50%);
		width: 1px;
		height: 32px;
		background: linear-gradient(to bottom, transparent 0%, rgba(148, 163, 184, 0.4) 50%, transparent 100%);
		pointer-events: none;

		@media (max-width: 768px) {
			right: -16px;
		}

		@media (max-width: 640px) {
			display: none;
		}
	}

	img {
		height: 52px;
		width: auto;
		display: block;
		transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
		filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1));

		&:hover {
			transform: scale(1.02);
			filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.15));
		}

		@media (max-width: 1024px) {
			height: 48px;
		}

		@media (max-width: 768px) {
			height: 44px;
		}

		@media (max-width: 640px) {
			height: 40px;
		}

		@media (max-width: 480px) {
			height: 38px;
		}
	}

	@media (max-width: 640px) {
		justify-content: flex-start;
	}
}

.filter-container {
	@include align-center;
	flex-wrap: nowrap;
	gap: 16px;
	justify-content: flex-start;
	position: relative;
	min-width: 0;
	flex: 1;
	overflow: visible;
	z-index: 2;

	&::before {
		content: '';
		position: absolute;
		inset: -8px;
		background: rgba(255, 255, 255, 0.6);
		border-radius: 16px;
		backdrop-filter: blur(10px);
		-webkit-backdrop-filter: blur(10px);
		border: 1px solid rgba(255, 255, 255, 0.2);
		z-index: -1;
		opacity: 0;
		transition: opacity 0.3s ease;
		pointer-events: none;
	}

	&:hover::before {
		opacity: 1;
	}

	@media (max-width: 768px) {
		gap: 16px;
	}

	@media (max-width: 640px) {
		gap: 10px;
	}

	@media (max-width: 480px) {
		gap: 8px;
	}
}

.header-filter-toolbar {
	align-items: center;
	width: 100%;
	min-width: 0;
}

.filter-time-grow {
	flex: 1 1 auto;
	min-width: 0;
	max-width: none;
}

.filter-button-wrapper {
	flex-shrink: 0;
	margin-inline-start: auto;
	display: flex;
	align-items: center;
	justify-content: flex-end;
}

.fade-enter-active {
	transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.fade-leave-active {
	transition: all 0.3s cubic-bezier(0.4, 0, 1, 1);
}

.fade-enter-from {
	opacity: 0;
	transform: translateY(-8px) scale(0.96);
}

.fade-leave-to {
	opacity: 0;
	transform: translateY(4px) scale(0.98);
}

.filter-button {
	position: relative;
	height: 42px;
	min-width: 42px;
	width: 42px;
	border-radius: 6px;
	cursor: pointer;
	background-color: #f8f9fa;
	border: 1px solid #e7e7e7;
	display: flex;
	align-items: center;
	justify-content: center;
	transition: all 0.2s ease;
	padding: 0;
	flex-shrink: 0;

	img {
		width: 18px;
		height: 18px;
		transition: transform 0.2s ease;
	}

	&:hover {
		background-color: #e1e7ec;

		img {
			transform: scale(1.1);
		}
	}

	&:active {
		transform: scale(0.96);
	}

	&.active-filter {
		background-color: #e1f2ff;
		border-color: #b8e0ff;
	}
}

.filter-badge {
	position: absolute;
	top: 6px;
	right: 6px;
	width: 8px;
	height: 8px;
	background-color: #3b82f6;
	border-radius: 50%;
	box-shadow: 0 0 0 2px white;
}

:global(.filter-container *:focus-visible) {
	outline: 2px solid rgba(59, 130, 246, 0.8);
	outline-offset: 3px;
	border-radius: 8px;
	box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.1);
}

@media (prefers-reduced-motion: reduce) {
	.header,
	.logo-container img,
	.fade-enter-active,
	.fade-leave-active {
		transition: none;
	}

	.header:hover {
		transform: none;
	}

	.logo-container img:hover {
		transform: none;
	}

	.header.loading::after {
		animation: none;
	}
}
</style>
